import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

function Alliances() {
  const [alliances, setAlliances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('xp');
  const [myAlliance, setMyAlliance] = useState(null);
  const [canCreate, setCanCreate] = useState(false);

  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    fetchAlliances();
    if (accountHash) {
      fetchMyAlliance();
    }
  }, [sort, search]);

  const fetchAlliances = async () => {
    try {
      const params = new URLSearchParams({ sort, limit: 50 });
      if (search) params.append('search', search);

      const response = await fetch(`/api/alliances?${params}`);
      const data = await response.json();

      if (data.success) {
        setAlliances(data.alliances);
      }
    } catch (err) {
      setError('Failed to load alliances');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAlliance = async () => {
    try {
      const response = await fetch(`/api/my-alliance/${accountHash}`);
      const data = await response.json();
      if (data.success) {
        setMyAlliance(data.alliance);
        setCanCreate(data.can_create);
      }
    } catch (err) {
      console.error('Failed to fetch my alliance');
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    const n = parseFloat(num);
    if (isNaN(n)) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return Math.round(n).toString();
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading alliances...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-content alliances-page">
        {/* Header */}
        <div className="page-header alliances-header">
          <div className="page-header-left">
            <h1>Eco Alliances</h1>
            <p className="page-subtitle">Join forces to maximize your environmental impact</p>
          </div>
          <div className="page-header-right">
            {myAlliance ? (
              <Link to={`/alliance/${myAlliance.id}`} className="my-alliance-btn">
                <span className="alliance-level-badge" style={{ background: myAlliance.banner_color }}>
                  Lv.{myAlliance.level}
                </span>
                <span className="alliance-name">{myAlliance.name}</span>
                {myAlliance.is_leader && <span className="leader-badge">Leader</span>}
              </Link>
            ) : accountHash && canCreate ? (
              <Link to="/alliances/create" className="create-alliance-btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Create Alliance
              </Link>
            ) : accountHash ? (
              <span className="create-hint">Reach Lv.5 to create an alliance</span>
            ) : null}
          </div>
        </div>

        {/* Controls */}
        <div className="alliances-controls">
          <div className="search-box">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              type="text"
              placeholder="Search alliances..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="sort-buttons">
            {['xp', 'level', 'members', 'co2'].map(s => (
              <button
                key={s}
                className={`sort-btn ${sort === s ? 'active' : ''}`}
                onClick={() => setSort(s)}
              >
                {s === 'xp' ? 'XP' : s === 'level' ? 'Level' : s === 'members' ? 'Members' : 'CO2'}
              </button>
            ))}
          </div>
        </div>

        {/* Alliances Grid */}
        {error ? (
          <div className="page-error">
            <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>{error}</p>
          </div>
        ) : alliances.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <h3>No alliances found</h3>
            <p>Be the first to create one!</p>
          </div>
        ) : (
          <div className="alliances-grid">
            {alliances.map((alliance) => (
              <Link
                to={`/alliance/${alliance.id}`}
                key={alliance.id}
                className="alliance-card"
              >
                <div className="alliance-card-header">
                  <span className="alliance-rank">#{alliance.rank}</span>
                  <div className="alliance-level-container">
                    <span className="alliance-level" style={{ background: alliance.banner_color }}>
                      Lv.{alliance.level}
                    </span>
                    <div className="alliance-level-bar">
                      <div
                        className="alliance-level-fill"
                        style={{
                          width: `${alliance.level_progress || 0}%`,
                          background: alliance.banner_color
                        }}
                      />
                    </div>
                  </div>
                </div>

                <h3 className="alliance-name">{alliance.name}</h3>
                <p className="alliance-desc">{alliance.description || 'No description'}</p>

                <div className="alliance-stats">
                  <div className="alliance-stat">
                    <span className="stat-num">{alliance.member_count}</span>
                    <span className="stat-label">members</span>
                  </div>
                  <div className="alliance-stat">
                    <span className="stat-num">
                      {alliance.co2_kg >= 1 ? formatNumber(alliance.co2_kg) : Math.round((alliance.co2_kg || 0) * 1000)}
                    </span>
                    <span className="stat-label">{alliance.co2_kg >= 1 ? 'kg' : 'g'} CO2</span>
                  </div>
                  <div className="alliance-stat">
                    <span className="stat-num online">{alliance.online_members}</span>
                    <span className="stat-label">online</span>
                  </div>
                </div>

                <div className="alliance-card-footer">
                  <span className="alliance-leader">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
                    </svg>
                    {alliance.leader_name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default Alliances;
